import { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useShadowStyles } from "../hooks/useShadowStyles";
import { useArticleCache } from "../hooks/useArticleCache";
import { buildTocDom } from "../hooks/useToc";
import { extractMoveData } from "../utils/messageParser";
import type { LinkData } from "../utils/messageParser";
import { setArticleRenderCallback } from "../lib/articleRenderQueue";
import { trackEvent } from "../lib/analytics";
import { scrollToElement } from "../lib/scroll";
import { getThemeColors } from "../lib/theme";
import { getStorage, setStorage } from "../lib/utils";
import { fetchWikipediaArticleHtml } from "../lib/wikipediaApi";
import { queueArticleRender } from "../lib/articleRenderQueue";

type MoveAction = { action: string; payload?: unknown };

const WIKIPAGE_FULL_WIDTH_STORAGE_KEY = "isWikipageFullWidth";
const EMPTY_HIDDEN_SECTION_TITLES: string[] = [];

export type WikipageInput = {
  html: string;
  articleTitle: string;
  articleId: number;
};

export type WikipageProps = {
  isDark?: boolean;
  wikipageInput?: WikipageInput;
  onCurrentArticleIdChange?: (articleId: number) => void;
  onLinkClick?: (data: LinkData) => void;
  onLinkHover?: (data: LinkData) => void;
  onRender?: () => void;
  onContentLoaded?: (anchor: string | null) => void;
  className?: string;
  send?: (action: MoveAction) => void;
  requestedArticleId?: number | null;
  articleRequestKey?: number;
  isLinkBlocked?: (articleId: number) => boolean | null;
  allowSearch?: boolean;
  hiddenSectionTitles?: string[];
  language?: string;
};

const scrollToTop = (container: HTMLElement) => {
  container.scrollTo({ top: 0, behavior: "instant" });
};

export const Wikipage = function Wikipage({
  isDark = false,
  onCurrentArticleIdChange,
  onLinkClick,
  onLinkHover,
  onRender,
  onContentLoaded,
  className = "",
  send,
  requestedArticleId,
  articleRequestKey,
  isLinkBlocked,
  allowSearch = false,
  hiddenSectionTitles = EMPTY_HIDDEN_SECTION_TITLES,
  language = "en",
}: WikipageProps) {
  const { t } = useTranslation();
  const theme = getThemeColors(isDark);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const contentElRef = useRef<HTMLDivElement | null>(null);
  const { styles } = useShadowStyles();
  const { get: getCached, set: setCached, has: hasCached } = useArticleCache();

  const isMountedRef = useRef(false);
  const futureArticleIdRef = useRef<number | null>(null);
  const pendingAnchorRef = useRef<string | null>(null);
  const hoverTimeoutsRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const HOVER_DELAY = 100;
  const handledArticleRequestRef = useRef<{
    articleId: number;
    key: number | undefined;
  } | null>(null);
  const [content, setContent] = useState<{
    html: string;
    articleTitle: string;
    articleId: number;
  } | null>(null);
  const [loadingArticleId, setLoadingArticleId] = useState<number | null>(null);
  const [hoverPreview, setHoverPreview] = useState<{
    title: string;
    body?: string;
    x: number;
    y: number;
    articleId: number;
  } | null>(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(
    () => getStorage(WIKIPAGE_FULL_WIDTH_STORAGE_KEY) === "true",
  );
  const isWindowFocusedRef = useRef(true);
  const savedScrollTopRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleWidthToggle = () => {
    const nextFullWidth = !isFullWidth;
    setIsFullWidth(nextFullWidth);
    setStorage(WIKIPAGE_FULL_WIDTH_STORAGE_KEY, String(nextFullWidth));
  };

  const setPendingAnchor = useCallback((anchor: string | null) => {
    pendingAnchorRef.current = anchor;
  }, []);

  useEffect(() => {
    if (!requestedArticleId) return;
    const handledArticleRequest = handledArticleRequestRef.current;
    if (
      handledArticleRequest?.articleId === requestedArticleId &&
      handledArticleRequest.key === articleRequestKey
    ) {
      return;
    }

    handledArticleRequestRef.current = {
      articleId: requestedArticleId,
      key: articleRequestKey,
    };

    futureArticleIdRef.current = requestedArticleId;
    pendingAnchorRef.current = null;

    const entry = getCached(requestedArticleId);
    if (entry) {
      setLoadingArticleId(null);
    } else if (requestedArticleId !== content?.articleId) {
      setLoadingArticleId(requestedArticleId);
    }

    if (entry && entry.articleId !== content?.articleId) {
      setContent(entry);
      setHoverPreview(null);
    }
  }, [articleRequestKey, content?.articleId, getCached, requestedArticleId]);

  useEffect(() => {
    setArticleRenderCallback((wikipageInput: WikipageInput) => {
      if (!futureArticleIdRef.current)
        futureArticleIdRef.current = wikipageInput.articleId;

      const cached = hasCached(wikipageInput.articleId);
      if (!cached) {
        setCached(wikipageInput.articleId, wikipageInput);
      }

      if (
        wikipageInput.articleId !== content?.articleId &&
        wikipageInput.articleId === futureArticleIdRef.current
      ) {
        setContent(
          cached
            ? (getCached(wikipageInput.articleId) ?? wikipageInput)
            : wikipageInput,
        );
        setLoadingArticleId(null);
        setHoverPreview(null);
      }
    });
    return () => {
      setArticleRenderCallback(null);
    };
  }, [content?.articleId, getCached, hasCached, setCached]);

  useEffect(() => {
    if (allowSearch) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        savedScrollTopRef.current = containerRef.current?.scrollTop ?? 0;
        isWindowFocusedRef.current = false;
        setIsSuspended(true);
      } else {
        isWindowFocusedRef.current = true;
        setIsSuspended(false);
      }
    };

    const handleBlur = () => {
      savedScrollTopRef.current = containerRef.current?.scrollTop ?? 0;
      isWindowFocusedRef.current = false;
      setIsSuspended(true);
    };

    const handleFocus = () => {
      isWindowFocusedRef.current = true;
      setIsSuspended(false);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [allowSearch]);

  useEffect(() => {
    if (allowSearch) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isSearch =
        (e.key === "f" && (e.ctrlKey || e.metaKey)) ||
        (e.key === "g" && (e.ctrlKey || e.metaKey)) ||
        e.key === "F3";

      if (isSearch) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [allowSearch]);

  const executeMove = useCallback(
    (articleId: number, articleTitle: string, anchor: string | null = null) => {
      const cached = hasCached(articleId);

      onCurrentArticleIdChange?.(articleId);

      if (cached) {
        const entry = getCached(articleId);
        if (entry) {
          setLoadingArticleId(null);
          setContent(entry);
          setHoverPreview(null);
        } else {
          setLoadingArticleId(articleId);
        }
      } else {
        setLoadingArticleId(articleId);
        const timeoutId = hoverTimeoutsRef.current;
        if (timeoutId) {
          clearTimeout(timeoutId);
          hoverTimeoutsRef.current = null;
        }
      }

      return {
        action: "move",
        payload: {
          id: articleId,
          name: articleTitle,
          anchor,
        },
      };
    },
    [getCached, hasCached, onCurrentArticleIdChange],
  );

  const loadArticle = useCallback(
    async (articleId: number, articleTitle: string) => {
      try {
        const html = await fetchWikipediaArticleHtml(articleId, language);
        if (!isMountedRef.current) return;

        queueArticleRender({ articleId, articleTitle, html });
      } catch (error) {
        if (!isMountedRef.current) return;

        console.error("Failed to load Wikipedia article", error);
        if (futureArticleIdRef.current === articleId) {
          futureArticleIdRef.current = null;
          pendingAnchorRef.current = null;
        }
        setLoadingArticleId((loadingId) =>
          loadingId === articleId ? null : loadingId,
        );
      }
    },
    [language],
  );

  const handleClick = useCallback(
    (e: Event) => {
      if (!allowSearch && !isWindowFocusedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const target = e.target as Element;
      const link = target.closest("a");
      if (!link) return;

      const hxVals = link.getAttribute("hx-vals");
      if (!hxVals) return;

      e.preventDefault();
      e.stopPropagation();

      const linkData = extractMoveData(hxVals);
      if (!linkData) return;

      futureArticleIdRef.current = linkData.id;

      const isSameArticle = Number(linkData.id) === Number(content?.articleId);

      if (isSameArticle && containerRef.current) {
        setLoadingArticleId(null);
        if (linkData.anchor && shadowRootRef.current) {
          const el = shadowRootRef.current.getElementById(linkData.anchor);
          if (el) {
            scrollToElement(containerRef.current, el);
          }
        } else {
          scrollToTop(containerRef.current);
        }
      } else {
        setPendingAnchor(linkData.anchor);
        const action = executeMove(linkData.id, linkData.name, linkData.anchor);
        void loadArticle(linkData.id, linkData.name);
        send?.(action);
        trackEvent("wiki-article-move", { articleName: linkData.name });
      }

      onLinkClick?.(linkData);
    },
    [
      content?.articleId,
      send,
      onLinkClick,
      executeMove,
      loadArticle,
      setPendingAnchor,
      allowSearch,
    ],
  );

  const handleMouseEnter = useCallback(
    (e: Event) => {
      if (!allowSearch && !isWindowFocusedRef.current) return;

      const target = e.target as Element;
      const link = target.closest("a");
      if (!link) return;

      const hxVals = link.getAttribute("hx-vals");
      if (!hxVals) return;

      const linkData = extractMoveData(hxVals);
      if (!linkData) return;

      const rect = link.getBoundingClientRect();

      const cached = hasCached(linkData.id);
      const body = link.getAttribute("data-description")?.trim() || undefined;

      const oldTimeoutId = hoverTimeoutsRef.current;
      if (oldTimeoutId) {
        clearTimeout(oldTimeoutId);
      }

      hoverTimeoutsRef.current = setTimeout(() => {
        setHoverPreview({
          title: linkData.name,
          body,
          x: rect.left,
          y: rect.bottom + 4,
          articleId: linkData.id,
        });

        if (!cached) {
          void loadArticle(linkData.id, linkData.name);
          onLinkHover?.(linkData);
        }
      }, HOVER_DELAY);
    },
    [hasCached, loadArticle, onLinkHover, allowSearch],
  );

  const handleMouseLeave = useCallback(() => {
    setHoverPreview(null);

    const timeoutId = hoverTimeoutsRef.current;
    if (timeoutId) {
      clearTimeout(timeoutId);
      hoverTimeoutsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    if (!shadowRootRef.current) {
      if (containerRef.current.shadowRoot) {
        shadowRootRef.current = containerRef.current.shadowRoot;
      } else {
        shadowRootRef.current = containerRef.current.attachShadow({
          mode: "open",
        });
      }
    }

    if (!contentElRef.current) {
      const contentDiv = document.createElement("div");
      contentDiv.className = "shadow-content";
      contentDiv.style.position = "relative";
      shadowRootRef.current.appendChild(contentDiv);
      contentElRef.current = contentDiv;
    }
  }, []);

  //stlyes useEffect before content so the styles is inserted befoer we render the content
  useEffect(() => {
    const shadow = containerRef.current?.shadowRoot;
    if (!styles || !shadow) return;

    const existingStyle = shadow.querySelector("style");
    if (existingStyle) {
      existingStyle.textContent = styles;
    } else {
      const styleEl = document.createElement("style");
      styleEl.textContent = styles;
      shadow.appendChild(styleEl);
    }
  }, [styles]);

  useEffect(() => {
    if (!containerRef.current || !contentElRef.current || !content || !styles)
      return;

    const contentEl = contentElRef.current;

    if (isSuspended) {
      contentEl.innerHTML = "";
      return;
    }
    // Add loading="lazy" and hide broken image icon completely
    // TODO: Move this to the wiki dump creation process to avoid client-side processing
    const htmlWithImageHandling = content.html.replace(
      /<img\s/gi,
      "<img loading=\"lazy\" onerror=\"this.src=''; this.style.visibility='hidden'; this.onerror=null;\" ",
    );

    contentEl.innerHTML = `
        <h1 id="article_name">${content.articleTitle}</h1>
        <div id="article_body" class="mw-parser-output content">${htmlWithImageHandling}</div>
      `;

    if (hiddenSectionTitles.length > 0) {
      const hiddenTitles = new Set(
        hiddenSectionTitles.map((title) => title.trim().toLowerCase()),
      );
      contentEl
        .querySelectorAll(
          'section[data-mw-section-id]:not([data-mw-section-id="0"])',
        )
        .forEach((section) => {
          const heading = section.querySelector(
            ":scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6",
          );
          if (
            heading &&
            hiddenTitles.has((heading.textContent ?? "").trim().toLowerCase())
          ) {
            section.remove();
          }
        });
    }

    if (!window.matchMedia("(max-width: 640px)").matches) {
      if (containerRef.current) {
        buildTocDom(contentEl, containerRef.current);
      }
    }

    const links = contentEl.querySelectorAll(
      'a[hx-vals], a[rel~="mw:WikiLink"][data-id]',
    );

    links.forEach((link) => {
      if (!link.hasAttribute("hx-vals")) {
        const articleId = Number(link.getAttribute("data-id"));
        const articleTitle =
          link.getAttribute("title") ??
          link.getAttribute("data-title") ??
          (articleId === content.articleId ? content.articleTitle : null);
        const fragment =
          link.getAttribute("data-fragment") ??
          (link.getAttribute("href")?.startsWith("#")
            ? link.getAttribute("href")
            : null);

        if (
          Number.isInteger(articleId) &&
          articleId >= 0 &&
          articleId <= 0xffffffff &&
          articleTitle
        ) {
          link.setAttribute(
            "hx-vals",
            JSON.stringify({
              id: articleId,
              name: articleTitle,
              anchor: fragment?.replace(/^#/, "") ?? null,
            }),
          );
        }
      }

      // TODO: remove title attribute when processign the page in wikiwriter, then make this loop conditional on isLinkBlocked again
      const title = link.getAttribute("title");
      if (title) {
        link.setAttribute("data-title", title);
      }
      link.removeAttribute("title");
      if (isLinkBlocked) {
        try {
          const data = JSON.parse(link.getAttribute("hx-vals")!);
          if (data.id && isLinkBlocked(data.id) === true) {
            link.removeAttribute("hx-vals");
          }
        } catch {
          // Ignore malformed hx-vals from article markup.
        }
      }
    });

    if (window.matchMedia("(max-width: 640px)").matches) {
      const sections = contentEl.querySelectorAll(
        'section[data-mw-section-id]:not([data-mw-section-id="0"])',
      );
      sections.forEach((section) => section.classList.remove("expanded"));

      const section0 = contentEl.querySelector(
        'section[data-mw-section-id="0"]',
      );
      if (section0) {
        const firstRealP = section0.querySelector("p:not(.mw-empty-elt)");
        if (firstRealP) {
          // class so that css can pull first p before infobox on mobile
          firstRealP.classList.add("mw-first-p");
        }
      }
    }

    const anchor = pendingAnchorRef.current;
    if (anchor) {
      pendingAnchorRef.current = null;
      const el = shadowRootRef.current?.getElementById(anchor);
      if (el && containerRef.current) {
        scrollToElement(containerRef.current, el);
      }
    } else if (savedScrollTopRef.current > 0 && containerRef.current) {
      containerRef.current.scrollTo({
        top: savedScrollTopRef.current,
        behavior: "instant",
      });
      savedScrollTopRef.current = 0;
    } else if (containerRef.current) {
      scrollToTop(containerRef.current);
    }

    onContentLoaded?.(anchor);
    onRender?.();
    setLoadingArticleId((currentArticleId) =>
      currentArticleId === content.articleId ? null : currentArticleId,
    );
  }, [
    content,
    hiddenSectionTitles,
    isLinkBlocked,
    onContentLoaded,
    onRender,
    styles,
    isSuspended,
  ]);

  const handleSectionToggle = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    const heading = target.closest("h2, h3, h4, h5, h6");
    if (!heading) return;

    const section = heading.closest(
      'section[data-mw-section-id]:not([data-mw-section-id="0"])',
    );
    if (!section) return;

    e.preventDefault();
    e.stopPropagation();
    const wasExpanded = section.classList.contains("expanded");
    section.classList.toggle("expanded");

    const descendantSections = section.querySelectorAll(
      "section[data-mw-section-id]",
    );
    descendantSections.forEach((s) => {
      if (wasExpanded) {
        s.classList.remove("expanded");
      } else {
        s.classList.add("expanded");
      }
    });

    if (!wasExpanded && containerRef.current) {
      scrollToElement(containerRef.current, section);
    }
  }, []);

  useEffect(() => {
    const contentEl = contentElRef.current;
    if (!contentEl) return;

    contentEl.addEventListener("click", handleClick);
    contentEl.addEventListener("mouseenter", handleMouseEnter, true);
    contentEl.addEventListener("mouseleave", handleMouseLeave, true);
    contentEl.addEventListener("click", handleSectionToggle, true);

    return () => {
      contentEl.removeEventListener("click", handleClick);
      contentEl.removeEventListener("mouseenter", handleMouseEnter, true);
      contentEl.removeEventListener("mouseleave", handleMouseLeave, true);
      contentEl.removeEventListener("click", handleSectionToggle, true);
    };
  }, [handleClick, handleMouseEnter, handleMouseLeave, handleSectionToggle]);

  // max-w-246 wikipedia size + margin
  const isLoading = loadingArticleId !== null;

  return (
    <div
      className={`relative h-full w-full ${isFullWidth ? "max-w-none" : "max-w-246"} ${className}`}
      aria-busy={isLoading}
      data-testid="wikipage-container"
    >
      <div
        className="group/width-toggle absolute right-3 top-3 z-20 hidden h-16 w-16 items-start justify-end sm:flex"
        data-testid="wikipage-width-toggle-proximity"
      >
        <button
          type="button"
          onClick={handleWidthToggle}
          className={`cursor-pointer rounded-lg border p-2 opacity-0 transition-[color,background-color,border-color,opacity] group-hover/width-toggle:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 ${theme.buttonSecondary} ${theme.buttonSecondaryText} ${theme.border}`}
          aria-label={
            isFullWidth
              ? t("components.wikipageWidth.limit")
              : t("components.wikipageWidth.expand")
          }
          aria-pressed={isFullWidth}
          data-testid="wikipage-width-toggle"
        >
          {isFullWidth ? (
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 12h16M8 8l4 4-4 4m8-8-4 4 4 4"
              />
            </svg>
          ) : (
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 8 4 12l4 4M4 12h16m-4-4 4 4-4 4"
              />
            </svg>
          )}
        </button>
      </div>
      <div
        ref={containerRef}
        className={`h-full w-full overflow-auto scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent ${isDark ? "dark-mode brightness-[0.9]" : ""}`}
        data-testid="wikipage-content"
      />
      {isSuspended && (
        <div
          className={`absolute inset-0 z-30 flex items-center justify-center ${isDark ? "bg-zinc-950" : "bg-gray-50"}`}
        >
          <p
            className={`text-2xl font-light tracking-wide ${isDark ? "text-zinc-500" : "text-gray-400"}`}
          >
            Focus this tab to play. Searching the wikipage is cheating.
          </p>
        </div>
      )}
      {isLoading && (
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden ${isDark ? "bg-white/10" : "bg-gray-900/10"}`}
        >
          <div
            className={`wikipage-loading-progress h-full w-1/3 rounded-full ${isDark ? "bg-white" : "bg-gray-900"}`}
          />
        </div>
      )}
      {typeof document !== "undefined" &&
        hoverPreview &&
        createPortal(
          <div
            className={`max-w-xs rounded-lg border p-3 text-xs leading-relaxed shadow-lg pointer-events-none ${isDark ? "bg-zinc-900 border-zinc-600 text-zinc-300" : "bg-white border-gray-200 text-gray-600"}`}
            data-testid="wikipage-hover-preview"
            style={{
              position: "fixed",
              left: hoverPreview.x,
              top: hoverPreview.y,
              zIndex: 9999,
            }}
          >
            <div
              className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {hoverPreview.title}
            </div>
            {hoverPreview.body && (
              <>
                <hr
                  className={`my-2 ${isDark ? "border-zinc-400" : "border-gray-900"}`}
                />
                {hoverPreview.body}
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};
